import {
  BoundMultiSelectField,
  Button,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SubmitButton,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { ArchivedTag } from "src/components/ArchivedTag";
import { CostType, Maybe, SaveTakeoffLineItemInput } from "src/generated/graphqlTypes";
import { AddEditItemLocationsField } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemLocationsField";
import { AddEditItemModal_LocationFragment } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";
import { SaveTliResult, TakeoffsStoreState } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { pluralize, queryResult } from "src/utils";
import { StoreApi } from "zustand";
import { useAddEditItemModal_GetReadyPlanQuery } from "./AddItems/AddEditItemModal.generated";
import { TakeoffTaskBoundSelectField } from "./AddItems/utils";
import { BulkEditModal_TliFragment, useBulkEditModal_GetTlisQuery } from "./BulkEditModal.generated";

type BulkEditModalProps = {
  store: StoreApi<TakeoffsStoreState>;
};

export function BulkEditModal({ store }: BulkEditModalProps) {
  const { planPackage, getSelectedTliIds } = store.getState();
  const selectedTliIds = getSelectedTliIds();
  const tlisQuery = useBulkEditModal_GetTlisQuery({
    variables: { filter: { id: selectedTliIds, readyPlan: [planPackage.id] }, versionId: planPackage.version.id },
  });
  return queryResult(tlisQuery, ({ takeoffLineItemsPage }) => (
    <BulkEditModalView store={store} tlis={takeoffLineItemsPage.items} />
  ));
}

type BulkEditModalViewProps = {
  store: StoreApi<TakeoffsStoreState>;
  tlis: BulkEditModal_TliFragment[];
};

function BulkEditModalView(props: BulkEditModalViewProps) {
  const { store, tlis } = props;
  const { saveTli, planPackage } = store.getState();
  const { closeModal } = useModal();
  const { data: rpData } = useAddEditItemModal_GetReadyPlanQuery({
    variables: { readyPlanId: planPackage.id, versionId: planPackage.version.id },
  });
  const formState = useFormState({
    config: formConfig,
  });

  const laborLines = tlis.filter((tli) => tli.costType === CostType.Labor);
  const linesWithoutLocationEdit = tlis.filter((tli) => !tli.canEditLocation.allowed);

  return (
    <>
      <ModalHeader>
        <span css={Css.lg.$}>Bulk Edit</span>
      </ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap1.pb3.$}>
          <AddEditItemLocationsField
            rpavId={store.getState().planPackage.version.id}
            newFeatureNameField={formState.newLocationName}
            field={formState.locationId}
            onSelect={(locationId: string | undefined, location) => {
              formState.locationId.set(locationId);
              formState.location.set(location);
            }}
          />
          {linesWithoutLocationEdit.nonEmpty ? (
            <div css={Css.sm.red500.$}>
              {linesWithoutLocationEdit.length} {pluralize(linesWithoutLocationEdit.length, "line")} selected won't be
              updated by the location field.
            </div>
          ) : null}
          <TakeoffTaskBoundSelectField formState={formState} readOnly={false} />
          {laborLines.nonEmpty ? (
            <div css={Css.sm.red500.$}>
              {laborLines.length} labor {pluralize(laborLines.length, "line is", "lines are")} selected,
              {pluralize(laborLines.length, " its task ", " thier tasks ")}
              won't be updated by the task field.
            </div>
          ) : null}
          {/* This is copied from AddItems/utils.tsx common fields  but without UOM and Qty fields */}
          {/* At some point if we revisit canEditOptions, we can add this back in */}
          <BoundMultiSelectField
            label="Options"
            field={formState.optionIds}
            options={rpData?.readyPlan?.options ?? []}
            getOptionLabel={(o) => o.displayName + (o.active ? "" : " (Archived)")}
            getOptionValue={(o) => o.id}
            getOptionMenuLabel={(o) => <ArchivedTag active={o.active}>{o.displayName}</ArchivedTag>}
          />
          {/* TODO: Add location field */}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} size="lg" />
        <SubmitButton
          label="Save"
          data-testid="saveBtn"
          form={formState}
          onClick={async () => {
            await saveBulkEdit({ formState, saveTli, tlis });
            closeModal();
          }}
          size="lg"
        />
      </ModalFooter>
    </>
  );
}

export type BulkEditObjectState = Omit<SaveTakeoffLineItemInput, "id"> & {
  location: Maybe<AddEditItemModal_LocationFragment>;
  newLocationName?: Maybe<string>;
};

const formConfig: ObjectConfig<BulkEditObjectState> = {
  locationId: { type: "value" },
  optionIds: { type: "value" },
  taskId: { type: "value" },

  // Store a new location path if needed
  newLocationName: { type: "value" },
  // Also store the more detailed objects for easier access/logic needs
  location: { type: "value" },
};

async function saveBulkEdit(params: {
  formState: ObjectState<BulkEditObjectState>;
  saveTli: (item: SaveTakeoffLineItemInput | SaveTakeoffLineItemInput[]) => SaveTliResult;
  tlis: BulkEditModal_TliFragment[];
}) {
  const { formState, saveTli, tlis } = params;

  const {
    newLocationName,
    // Pulling out location here so that it does not end up in rest
    location: changedLocation,
    locationId,
    ...rest
  } = formState.changedValue;

  // The user might have changed the Feature (newLocationName) but not the parent room (locationId),
  // so make sure to get the location directly from the `formState` and not `changedValue`
  const location = formState.location.value;

  await saveTli(
    tlis.map((tli) => {
      return {
        id: tli.id,
        ...rest,
        taskId: tli.costType === CostType.Labor ? undefined : rest.taskId,
        ...(tli.canEditLocation.allowed
          ? newLocationName
            ? {
                // Add the new feature onto the existing location path
                locationPath: `${(location ?? fail("Trying to create a new location without a location object")).path}~f:${newLocationName}`,
              }
            : locationId
              ? { locationId }
              : {}
          : {}),
      };
    }),
  );

  // We thought we didn't need this (getting reset by the apollo cache), but the form state is staying dirty
  formState.commitChanges();
}
